import React, { useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

import { useSocieteRewardOverrides } from '~/hooks/apis/rewards/useSocieteReward';

import type { SocieteRewardOverrideListRow, SocieteRewardStatus } from '~/services/LuxLoyalty';

import { flashRewardsOfferUrl } from './flashRewardsUrl';
import { formatExtraNightRate } from './formatExtraNightRate';
import { isSocieteRewardEligible } from './isSocieteRewardEligible';

/** Fixed page size for the overrides audit table — `TablePagination`'s rows-per-page control is hidden. */
const PAGE_SIZE = 20;

/**
 * Maps a Société reward status to the MUI Chip color used to badge it.
 * Mirrors the convention in `SocieteRewardSummary`: either eligible value (AUTO_ELIGIBLE,
 * ADMIN_ELIGIBLE) -> success, ADMIN_INELIGIBLE -> error, AUTO_INELIGIBLE -> default.
 */
function statusChipColor(status: SocieteRewardStatus): 'success' | 'default' | 'error' {
  if (isSocieteRewardEligible(status)) return 'success';
  if (status === 'ADMIN_INELIGIBLE') return 'error';
  return 'default';
}

/**
 * Renders one row's per-currency copay summary as "CCY base/extra" lines.
 * A `null` copay value (base or extra-night) renders as "—" rather than being switched on.
 */
function CopayCell({ row }: { row: SocieteRewardOverrideListRow }) {
  if (row.prices.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25}>
      {row.prices.map((price) => (
        <Typography key={price.currency_code} variant="body2">
          {price.currency_code} {price.societe_reward_copay_base ?? '—'}/{price.societe_reward_copay_extra_night ?? '—'}
        </Typography>
      ))}
    </Stack>
  );
}

/**
 * Renders the override audit columns (field chips, by, when, reason) for one row.
 * `override_meta` is `null` for a row that isn't currently overridden — renders an em dash for
 * each cell instead of crashing.
 */
function OverrideMetaCells({ row }: { row: SocieteRewardOverrideListRow }) {
  if (!row.override_meta) {
    return (
      <>
        <TableCell>—</TableCell>
        <TableCell>—</TableCell>
        <TableCell>—</TableCell>
        <TableCell>—</TableCell>
      </>
    );
  }

  const { by, at, reason, fields } = row.override_meta;
  const whenDisplay = new Date(at).toLocaleString();

  return (
    <>
      <TableCell>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {/* Override meta fields are rendered verbatim — never switched on known values — so an
              unanticipated future field (e.g. "copay:EUR") still displays correctly. */}
          {fields.map((field) => (
            <Chip key={field} label={field} size="small" variant="outlined" />
          ))}
        </Stack>
      </TableCell>
      <TableCell>{by}</TableCell>
      <TableCell>{whenDisplay}</TableCell>
      <TableCell>{reason}</TableCell>
    </>
  );
}

/**
 * Admin, read-only audit tab listing every Société flash-hotel package whose reward currently
 * carries a manual override (or has an override history) — "Flash Overrides" under the Société
 * tab group. Each row links back to the offer and to the Flash Rewards management page so an
 * admin can drill in and act.
 *
 * Pagination is server-driven: `page` state maps to `offset = page * PAGE_SIZE` into
 * `useSocieteRewardOverrides`, which the hook re-fetches per page. Rows-per-page is fixed at 20
 * (not user-adjustable), so `TablePagination`'s rows-per-page selector is effectively hidden by
 * passing it a single option.
 */
export default function FlashRewardsOverridesPage() {
  const [page, setPage] = useState(0);
  const offset = page * PAGE_SIZE;

  const overridesQuery = useSocieteRewardOverrides({ limit: PAGE_SIZE, offset });
  const rows = overridesQuery.data?.result ?? [];
  const total = overridesQuery.data?.total ?? 0;

  return (
    <Stack spacing={3} sx={{ py: 3 }}>
      <Typography variant="h5">Flash Overrides</Typography>
      <Typography variant="body2" color="text.secondary">
        Read-only audit of Société flash-hotel packages with a manually overridden reward.
      </Typography>

      {overridesQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {overridesQuery.isError && (
        <Alert severity="error">
          {overridesQuery.error instanceof Error ? overridesQuery.error.message : 'Failed to load overrides.'}
        </Alert>
      )}

      {overridesQuery.isSuccess && total === 0 && (
        <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
          No overridden packages
        </Typography>
      )}

      {overridesQuery.isSuccess && total > 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Offer</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Overridden fields</TableCell>
                <TableCell>By</TableCell>
                <TableCell>When</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Points (base / extra night)</TableCell>
                <TableCell>Copay</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.offer_id_salesforce_external}:${row.package_id}`}>
                  <TableCell>
                    <Link to={`/offers/${row.offer_id_salesforce_external}`}>
                      {row.offer_name ?? row.offer_id_salesforce_external}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0}>
                      <Typography variant="body2">{row.package_name ?? row.package_id}</Typography>
                      {row.package_id_salesforce_external && (
                        <Typography variant="caption" color="text.secondary">
                          {row.package_id_salesforce_external}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <OverrideMetaCells row={row} />
                  <TableCell>
                    {row.points_base} / {formatExtraNightRate(row.points_extra_night)}
                  </TableCell>
                  <TableCell>
                    <CopayCell row={row} />
                  </TableCell>
                  <TableCell>
                    <Chip label={row.status} color={statusChipColor(row.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Link to={flashRewardsOfferUrl(row.offer_id_salesforce_external)}>Open in Flash Hotel Rewards</Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onRowsPerPageChange={() => {
              // Rows-per-page is fixed — the selector is hidden since rowsPerPageOptions has a
              // single entry, so this is never actually invoked.
            }}
          />
        </>
      )}
    </Stack>
  );
}
